import {
  CameraShake,
  Center,
  Environment,
  OrbitControls,
  PerspectiveCamera,
  PivotControls,
  Preload,
  useGLTF,
  View,
} from '@react-three/drei';
import { Canvas, GroupProps, PrimitiveProps, useFrame } from '@react-three/fiber';
import * as React from 'react';
import { Group, Mesh } from 'three';
import Area from './Area';
import { keyframes } from '@emotion/react';

export type Props = {};

declare module 'three-stdlib/loaders/GLTFLoader' {
  interface GLTF {
    nodes: {
      [key: string]: Mesh;
      // Mesh_sodaBottle: {
      //   geometry: ;
      // };
      // Mesh_sodaBottle_1: {
      //   geometry: any;
      // };
    };
  }
}

function useHover() {
  const [hovered, hover] = React.useState(false);
  return [hovered, { onPointerOver: () => hover(true), onPointerOut: () => hover(false) }] as const;
}

function Soda(props: GroupProps) {
  const ref = React.useRef<Group>(null);
  const [hovered, spread] = useHover();
  const { nodes, materials } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/soda-bottle/model.gltf'
  );
  useFrame((state, delta) => {
    const group = ref.current;
    if (!group) {
      return;
    }
    group.rotation.y = (group.rotation.y || 0) + delta;
  });
  const node = nodes.Mesh_sodaBottle as Mesh;
  return (
    <group ref={ref} {...props} {...spread} dispose={null}>
      <mesh geometry={node.geometry}>
        <meshStandardMaterial color={hovered ? 'red' : 'green'} roughness={0.33} metalness={0.8} envMapIntensity={2} />
      </mesh>
      <mesh geometry={node.geometry} material={materials.red} material-envMapIntensity={0} />
    </group>
  );
}

export function Duck(props: Omit<PrimitiveProps, 'object'>) {
  const { scene } = useGLTF('https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/duck/model.gltf');
  return <primitive object={scene} {...props} />;
}

export function Candy(props: Omit<PrimitiveProps, 'object'>) {
  const { scene } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/candy-bucket/model.gltf'
  );
  useFrame((state, delta) => (scene.rotation.z = scene.rotation.y += delta));
  return <primitive object={scene} {...props} />;
}

export function Flash(props: Omit<PrimitiveProps, 'object'>) {
  const { scene } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/lightning/model.gltf'
  );
  useFrame((state, delta) => (scene.rotation.y += delta));
  return <primitive object={scene} {...props} />;
}

export function Apple(props: Omit<PrimitiveProps, 'object'>) {
  const { scene } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/apple-half/model.gltf'
  );
  useFrame((state, delta) => (scene.rotation.y += delta));
  return <primitive object={scene} {...props} />;
}

export function Target(props: Omit<PrimitiveProps, 'object'>) {
  const { scene } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/target-stand/model.gltf'
  );
  useFrame((state, delta) => (scene.rotation.y += delta));
  return <primitive object={scene} {...props} />;
}

function Link({ href, text, children }: { href: string; text: string; children: React.ReactNode }) {
  const ref = React.useRef<HTMLAnchorElement>(null);
  const [hovered, hover] = React.useState(false);
  console.log('Link', { hovered });
  return (
    <a
      href={href}
      onPointerOver={() => hover(true)}
      onPointerOut={() => hover(false)}
      onPointerMove={(e) => {
        const x = e.nativeEvent.offsetX;
        const target = e.target as HTMLElement;
        const y = e.nativeEvent.offsetY - ((target && target.offsetTop) || 0) - 100;
        ref.current && (ref.current.style.transform = `translate3d(${x}px,${y}px,0)`);
      }}
    >
      {text}
      <View
        ref={ref}
        visible={hovered}
        index={Infinity} // Render this view on top of all others
        className="view"
        style={{ position: 'absolute', width: 200, display: 'block', pointerEvents: 'none' }}
      >
        <group>{children}</group>
      </View>
    </a>
  );
}

function Common({ color }: { color?: string }) {
  return (
    <>
      {color && <color attach="background" args={[color]} />}
      <ambientLight intensity={0.5} />
      <pointLight position={[20, 30, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} color="blue" />
      <Environment preset="dawn" />
      <PerspectiveCamera makeDefault fov={40} position={[0, 0, 6]} />
    </>
  );
}

const ANIM_SCALE = keyframes`
  0% {
    transform: scale(0.3, 0.7);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.3, 0.7);
  }
`;

const ANIM_TRANSLATE_X = keyframes`
  0% {
    transform: translate3d(-100px, 0, 0);
  }
  25% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(100px, 0, 0);
  }
  75% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-100px, 0, 0);
  }
`;

const ANIM_TRANSLATE_Y = keyframes`
  0% {
    transform: translate3d(0, -100px, 0);
  }
  25% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, 100px, 0);
  }
  75% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, -100px, 0);
  }
`;

function App01(props: Props) {
  const {} = props;

  const refRoot = React.useRef<HTMLDivElement>(null);

  return (
    <Area
      css={{
        'padding': '2.5rem',
        'overflowY': 'scroll',
        '.view': {
          boxSizing: 'content-box',
          margin: '0.2rem',
          width: '400px',
          height: '200px',
          display: 'inline-block',
          overflow: 'hidden',
          border: '6px solid red',
        },
        '.scale': {
          animation: `${ANIM_SCALE} 5s ease-in-out 0s infinite`,
        },
        '.translateX': {
          animation: `${ANIM_TRANSLATE_X} 10s ease-in-out 0s infinite`,
        },
        '.translateY': {
          animation: `${ANIM_TRANSLATE_Y} 5s ease-in-out 0s infinite`,
        },
      }}
      ref={refRoot}
    >
      <div style={{ position: 'relative' }}>
        {/** Regular HTML with canvas bits mixed into it (<View>) */}
        <div className="text">
          Work on{' '}
          <Link href="https://github.com/pmndrs/react-three-fiber/releases/tag/v8.0.0" text="version 8">
            <Common />
            <Center>
              <Target scale={1.5} />
            </Center>
          </Link>{' '}
          has begun 3 Sep 2021.
          <View className="view translateX">
            <Common color="lightpink" />
            <PivotControls
              lineWidth={3}
              depthTest={false}
              // displayValues={false}
              scale={2}
            >
              <Soda scale={6} position={[0, -1.6, 0]} />
            </PivotControls>
            <OrbitControls makeDefault />
          </View>
          This is perhaps the biggest update to Fiber yet.
          <View className="view scale" style={{ height: 300 }}>
            <Common color="lightblue" />
            <Apple position={[0, -1, 0]} scale={14} />
            <OrbitControls makeDefault />
          </View>
          We've tried our best to keep breaking-changes to a minimum,
          <View className="view translateY">
            <Common color="lightgreen" />
            <Duck scale={2} position={[0, -1.6, 0]} />
            <CameraShake intensity={2} />
          </View>
          they mostly affect rarely used api's like attach.
          <View className="view scale">
            <Common color="peachpuff" />
            <Candy scale={3} />
          </View>
          This release brings a ton of performance related fixes,
          <View className="view translateX">
            <Common color="orange" />
            <Flash scale={3} />
          </View>
          but also includes some new and ground-breaking features.
        </div>
        {/** Fixed fullscreen canvas on top of everything, events tied to index root */}
        <Canvas
          style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, overflow: 'hidden', pointerEvents: 'none' }}
          eventSource={refRoot.current || undefined}
        >
          <View.Port />
          <Preload all />
        </Canvas>
      </div>
    </Area>
  );
}

export default React.memo(App01);
